import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { client } from '@/lib/api';

/**
 * PRD §F4: "Sync gap or token expiry produces a visible in-app banner, never
 * silent failure." A planner who does not know sync stopped is worse off than
 * one with no product at all — they are trusting a system that is not watching.
 *
 * Rendered globally in the layout, and deliberately silent when everything is
 * fine: a banner that is always present is a banner nobody reads.
 */
export function useGmailStatus() {
  return useQuery({
    queryKey: ['gmail-status'],
    queryFn: async () => {
      const res = await client.api.gmail.status.$get();
      if (!res.ok) throw new Error('Failed to fetch Gmail status');
      return res.json();
    },
    // Cheap metadata-only query; polling it keeps the banner honest without the
    // planner having to reload.
    refetchInterval: 60_000,
  });
}

/**
 * Two screens carry a Gmail card of their own, and the banner defers to both.
 *
 * The first-run page asks for the connection itself, in a setup card with a
 * Connect button and a done state. A thin bar two rows above it, naming the
 * same gap and pointing at Settings, reads as a second and separate chore
 * (EVE-44).
 *
 * Settings *is* the page every variant links to, so there the banner's only
 * affordance leads back to where the planner already is — Lily clicked
 * Reconnect and arrived on the screen she had opened to reconnect from
 * (EVE-66). The card below it names the signed-out account and reconnects in
 * one click, which is the whole of what the banner was pointing at.
 *
 * The whole banner goes in both places, not only the variant that prompted it.
 * Every state it reports — signed out, sync error, backfilling, stale — is a
 * statement about the mailbox that the card is already reporting on. It returns
 * on every other route, where the card is not there to speak for it.
 */
const SILENT_ROUTES = ['/welcome', '/settings'];

export default function GmailBanner() {
  const { pathname } = useLocation();
  const { data } = useGmailStatus();
  if (SILENT_ROUTES.includes(pathname)) return null;
  if (!data) return null;

  if (!data.connected) {
    return (
      <Banner tone="neutral">
        <span>
          Gmail isn't connected yet — inquiries can't be sent and replies won't be tracked.
        </span>
        <Link to="/settings" className="font-medium underline underline-offset-2">
          Connect Gmail
        </Link>
      </Banner>
    );
  }

  if (data.syncStatus === 'disconnected') {
    return (
      <Banner tone="warning">
        <AlertTriangle className="size-4 shrink-0" />
        <span>
          Google signed <strong>{data.email}</strong> out — this happens on a schedule while the
          app is in Testing. Replies aren't being tracked until you reconnect.
        </span>
        <Link to="/settings" className="font-medium underline underline-offset-2">
          Reconnect
        </Link>
      </Banner>
    );
  }

  if (data.syncStatus === 'error') {
    return (
      <Banner tone="error">
        <AlertTriangle className="size-4 shrink-0" />
        <span>Sync problem: {data.syncError ?? 'unknown error'}</span>
        <Link to="/settings" className="font-medium underline underline-offset-2">
          Details
        </Link>
      </Banner>
    );
  }

  if (data.syncStatus === 'backfilling') {
    return (
      <Banner tone="neutral">
        <RefreshCw className="size-4 shrink-0 animate-spin" />
        <span>
          Finishing setup for {data.email}. VendorOps tracks replies to inquiries you send
          from here — conversations already underway stay in your inbox.
        </span>
      </Banner>
    );
  }

  // Connected but quiet for a long time. The poller runs every 60s, so an hour
  // of silence means something is wrong that did not throw.
  if (data.staleMinutes !== null && data.staleMinutes > 60) {
    return (
      <Banner tone="warning">
        <AlertTriangle className="size-4 shrink-0" />
        <span>
          Last synced {data.staleMinutes} minutes ago — newer than that hasn't been checked.
        </span>
      </Banner>
    );
  }

  return null;
}

function Banner({
  tone,
  children,
}: {
  tone: 'neutral' | 'warning' | 'error';
  children: React.ReactNode;
}) {
  const styles = {
    neutral: 'bg-[var(--color-surface-container-high)] text-foreground',
    warning: 'bg-[var(--color-tertiary-container)] text-foreground',
    error: 'bg-destructive/10 text-destructive',
  }[tone];

  return (
    <div className={`flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-4 py-2 text-center text-sm md:px-6 ${styles}`}>
      {children}
    </div>
  );
}
